import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spin } from "antd";
import useDocumentLoader from "../../hooks/useDocumentLoader";
import useWebViewerInstances from "../../hooks/useWebViewerInstances";
import { applyApryseFeatureRestrictions } from "./DocumentUtils";
import {
  applyPageJump,
  applySearchHighlightWithLadder,
} from "../../utils/citationJump";

// Distinguishes concurrent mounts (e.g. two viewers on one screen);
// deliberately NOT derived from the document, so one mounted viewer
// keeps one Apryse engine for its whole life no matter how many
// documents pass through it.
let viewerSequence = 0;

const ReadOnlyDocumentViewer = ({
  documentId,
  filename = "",
  fileType = "",
  // Citation deep-link support (BE PR #1794): open at this 1-based
  // page…
  initialPage = null,
  // …and highlight this quote via the shared search ladder. Highlight
  // is best-effort: it searches the PDF's own text layer, so on
  // scanned pages with garbage OCR it may not land — the page jump
  // still does.
  highlightQuery = "",
  // Hands the live Apryse instance to the parent once the document is
  // loaded, so surrounding chrome (e.g. the extracted-text search bar
  // in GoldStandardViewerModal) can drive page jumps.
  onInstanceReady = null,
  // Overrides the default document_id -> presigned-URL resolution with
  // a caller-supplied resolver (e.g. Fee Notices' unsorted-mail letters,
  // which resolve through the mail-intake item's view_url instead of a
  // Document record). Must return/resolve the same {url, isDirect,
  // withCredentials, fileName?, contentType?} shape as
  // useDocumentLoader's resolveDocumentUrl. Read via ref (same pattern
  // as onInstanceReady above) so a caller passing a fresh arrow function
  // every render never tears down and re-boots the Apryse instance.
  resolveSource = null,
}) => {
  const [viewerContainer, setViewerContainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const generationRef = useRef(0);
  // Read at document-load time via a ref so prop churn (e.g. a parent
  // re-render recreating the onInstanceReady closure) never tears down
  // and re-boots the Apryse instance below.
  const deepLinkRef = useRef({ initialPage, highlightQuery, onInstanceReady });
  deepLinkRef.current = { initialPage, highlightQuery, onInstanceReady };
  const resolveSourceRef = useRef(resolveSource);
  resolveSourceRef.current = resolveSource;
  const { loadDocument, resolveDocumentUrl } = useDocumentLoader();
  const webViewerInstances = useWebViewerInstances();

  const documentData = useMemo(
    () => ({
      id: documentId,
      name: filename,
      file_type:
        fileType ||
        String(filename || "")
          .split(".")
          .pop()
          .toLowerCase(),
    }),
    [documentId, fileType, filename]
  );
  // One engine per MOUNT, not per document (2026-08-20, Matthew:
  // "whatever makes docs lickety split in a matter should also be
  // happening here"). The matter viewer is fast because TabView keeps
  // its Apryse instances alive and only swaps what's visible; keying
  // the instance to the document id meant every letter in the fee
  // notice queue paid a full WASM boot. The engine now lives exactly
  // as long as this component; documents are swapped into it below.
  const tabId = useMemo(() => `read-only-viewer-${++viewerSequence}`, []);
  // The boot promise bridges the two effects: the engine effect starts
  // it, the per-document effect awaits it. A ref (not state) because
  // its identity changing must not re-run either effect.
  const instancePromiseRef = useRef(null);

  useEffect(() => {
    if (!viewerContainer) return undefined;

    webViewerInstances.registerContainer(tabId, viewerContainer);
    instancePromiseRef.current = webViewerInstances
      .initializeTabInstance(tabId, viewerContainer)
      .then((instance) => {
        if (!instance) {
          throw new Error("The document reader could not be initialized.");
        }
        instance.UI.setToolbarGroup("toolbarGroup-View");
        instance.Core.annotationManager.enableReadOnlyMode();
        applyApryseFeatureRestrictions(instance);
        instance.UI.disableElements([
          "toolbarGroup-Annotate",
          "toolbarGroup-Edit",
          "toolbarGroup-Shapes",
          "toolbarGroup-Insert",
          "toolbarGroup-Forms",
          "toolbarGroup-FillAndSign",
        ]);
        return instance;
      });
    // Swallow here only to avoid an unhandled-rejection report; the
    // per-document effect awaits the same promise and surfaces the
    // failure in the viewer chrome.
    instancePromiseRef.current.catch(() => {});

    return () => {
      instancePromiseRef.current = null;
      webViewerInstances.disposeTabInstance(tabId);
      webViewerInstances.registerContainer(tabId, null);
    };
  }, [viewerContainer, tabId, webViewerInstances]);

  useEffect(() => {
    if (!viewerContainer || !documentId) return undefined;

    const generation = ++generationRef.current;
    let active = true;
    setLoading(true);
    setError("");

    // This is the same path used by the general Matter document viewer:
    // resolve the source while the engine boots (first document) or
    // immediately (the engine is already live for every later one).
    // A caller-supplied resolver (see `resolveSource` above) takes
    // priority; loadDocument() still falls back to resolveDocumentUrl
    // internally if that resolver's promise rejects or yields no URL.
    const resolvedUrlPromise = resolveSourceRef.current
      ? Promise.resolve(resolveSourceRef.current(documentData))
      : resolveDocumentUrl(documentData);

    Promise.resolve(instancePromiseRef.current)
      .then(async (instance) => {
        if (!instance) {
          throw new Error("The document reader could not be initialized.");
        }
        if (!active || generation !== generationRef.current) {
          return;
        }
        webViewerInstances.updateTabDocumentData(tabId, documentData);

        // A previous letter may still be open in this engine; close it
        // so loadDocument performs a real load (it treats an already-
        // loaded document as "done").
        const { documentViewer } = instance.Core;
        if (documentViewer.getDocument()) {
          await documentViewer.closeDocument();
        }
        if (!active || generation !== generationRef.current) {
          return;
        }

        await loadDocument(instance, documentData, {
          // The loader's duplicate-load guard keys on this id and only
          // releases when a load settles. Scope it to the DOCUMENT: two
          // loads of the same letter still dedupe, but a slow letter
          // abandoned mid-load can never lock the next letter out of
          // the shared engine while its 30s timeout runs down.
          tabId: `${tabId}:${documentId}`,
          resolvedUrlPromise,
          isCancelled: () => !active || generation !== generationRef.current,
          onDocumentLoaded: () => {
            if (active && generation === generationRef.current) {
              setLoading(false);
              const deepLink = deepLinkRef.current;
              if (deepLink.initialPage != null && deepLink.initialPage >= 1) {
                applyPageJump(instance, deepLink.initialPage);
              }
              if (deepLink.highlightQuery) {
                applySearchHighlightWithLadder(
                  instance,
                  deepLink.highlightQuery
                );
              }
              if (typeof deepLink.onInstanceReady === "function") {
                deepLink.onInstanceReady(instance);
              }
            }
          },
          onLoadError: (loadError) => {
            if (active && generation === generationRef.current) {
              setError(
                loadError?.message || "The document preview could not be loaded."
              );
              setLoading(false);
            }
          },
        });
      })
      .catch((viewerError) => {
        if (!active || generation !== generationRef.current) return;
        setError(
          viewerError?.message || "The document reader could not be initialized."
        );
        setLoading(false);
      });

    return () => {
      // Do NOT dispose here: the engine outlives any one document. The
      // engine effect above owns disposal (on unmount); this cleanup
      // only invalidates in-flight work for the outgoing document.
      active = false;
      generationRef.current += 1;
    };
  }, [
    documentData,
    documentId,
    loadDocument,
    resolveDocumentUrl,
    tabId,
    viewerContainer,
    webViewerInstances,
  ]);

  return (
    <div
      style={{ width: "100%", height: "100%", position: "relative" }}
      data-testid="read-only-document-viewer"
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.82)",
          }}
        >
          <Spin size="large" />
          <span>Loading document…</span>
        </div>
      )}
      {error && (
        <div
          style={{
            position: "absolute",
            inset: 16,
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Alert type="error" showIcon message="Preview unavailable" description={error} />
        </div>
      )}
      <div
        ref={setViewerContainer}
        style={{ width: "100%", height: "100%" }}
        data-testid="read-only-document-viewer-container"
      />
    </div>
  );
};

export default ReadOnlyDocumentViewer;
